import { useCallback, useEffect, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { RowDoubleClickedEvent } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { makeStyles } from "@material-ui/core";
import { ConsultantSearchCardDataFragment, SortOrder } from "@bonliva-crm/graphql";
import { DataGrid, usePolling } from "@bonliva-crm/ui";
import { useGlobalStore } from "@hooks";
import { CONSULTANT_SLIDER, ConsultantProfileLocation } from "@navigation";
import { useInitialColumnDefs } from "./use-initial-column-defs";
import { useConsultantSearchFilters } from "../../hooks/use-consultant-search-filters";
import { ConsultantTableTopBar } from "./consultant-table-top-bar";
import { useConsultantTableDatasource } from "./use-consultant-table-datasource";
import { QualityNoteModal, QualityNoteModalData } from "./quality-note-modal";
import { isCellRendererLink } from "@common/renderers";
import { ConsultantTableColumns } from "@bonliva-crm/shared";

export type ConsultantTableContext = {
  highlightText: string;
  setQualityNoteData: (data: QualityNoteModalData) => void;
};

export const ConsultantTable = () => {
  const {
    queryInput,
    columnNameSelectorHook: { columnNameSelector },
    filterManager
  } = useConsultantSearchFilters();
  const gridRef = useRef<AgGridReact>(null);
  const classes = useStyles();
  const { navigation } = useGlobalStore();
  const [qualityNoteData, setQualityNoteData] = useState(null);

  const queryInputRef = useRef<any>();

  const sortBy = filterManager?.formMethods?.watch("sort.sortBy");

  useEffect(() => {
    queryInputRef.current = queryInput;
  }, [queryInput]);

  useEffect(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.setColumnsVisible(
        ["Comments"],
        Boolean(queryInput.fullTextSearch) && Boolean(queryInput.text)
      );
    }
  }, [queryInput.fullTextSearch, queryInput.text]);

  const colDefs = useInitialColumnDefs(queryInput.fullTextSearch);

  usePolling(
    () => {
      gridRef.current?.api?.refreshInfiniteCache();
    },
    "/consultants",
    { interval: 5000 }
  );

  const onRowClicked = useCallback(
    ({ data, event }: RowDoubleClickedEvent<ConsultantSearchCardDataFragment>) => {
      const target = event.target as HTMLElement;

      if (
        target &&
        !isCellRendererLink(target) &&
        (event.defaultPrevented || target.tagName === "A")
      ) {
        return;
      }

      return navigation.open(
        CONSULTANT_SLIDER,
        new ConsultantProfileLocation(data._id)
      );
    },
    [navigation]
  );

  const datasource = useConsultantTableDatasource({
    getGridRef: () => gridRef.current
  });

  const onQualityNoteModalClose = useCallback(() => {
    setQualityNoteData(null);
  }, []);

  return (
    <div className={classes.root}>
      <DataGrid
        ref={gridRef}
        columnDefs={colDefs}
        rowSelection={"multiple"}
        onRowClicked={onRowClicked}
        suppressRowClickSelection={true}
        datasource={datasource}
        rowModelType={"infinite"}
        maxBlocksInCache={1}
        cacheBlockSize={20}
        paginationPageSize={20}
        paginationPageSizeSelector={[10, 20]}
        pagination={true}
        getRowId={(p) => p.data._id}
        animateRows={true}
        rowHeight={70}
        getRowClass={(params) => {
          if (params.data?.Unfit) {
            return classes.unfitRow;
          }
        }}
        columnNameSelector={columnNameSelector}
        suppressCellFocus={true}
        topBar={<ConsultantTableTopBar />}
        context={
          {
            highlightText: queryInput.text,
            setQualityNoteData
          } as ConsultantTableContext
        }
        gridOptions={{
          onSortChanged: (e) => {
            const columnState = e.api.getColumnState();
            const sortState = columnState
              .filter((col) => col.sort)
              .map((col) => ({
                colId: col.colId,
                sort: col.sort,
                sortIndex: col.sortIndex
              }));
            if (queryInputRef.current.text?.length > 0) {
              gridRef.current!.api?.applyColumnState({
                defaultState: { sort: null }
              });
              filterManager.formMethods.setValue("sort", {
                sortBy: null,
                sortOrder: null
              });
              return;
            }

            if (sortState?.length === 0) {
              filterManager.formMethods.setValue("sort", {
                sortBy: ConsultantTableColumns.Name,
                sortOrder: SortOrder.Asc
              });
            }
            if (sortState[0]?.colId && sortState[0]?.sort) {
              filterManager.formMethods.setValue("sort", {
                sortBy: sortState[0]?.colId as ConsultantTableColumns,
                sortOrder: sortState[0]?.sort === "asc" ? SortOrder.Asc : SortOrder.Desc
              });
            }
          }
        }}
        sortedColumn={sortBy}
        setDefaultSort={() => {
          gridRef.current!.api?.applyColumnState({
            defaultState: { sort: null }
          });
          filterManager.formMethods.setValue("sort", {
            sortBy: ConsultantTableColumns.Name,
            sortOrder: SortOrder.Asc
          });
        }}
      />

      <QualityNoteModal data={qualityNoteData} onClose={onQualityNoteModalClose} />
    </div>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100%",
    paddingBottom: "1rem"
  },
  unfitRow: {
    backgroundColor: theme.palette.error.light
  }
}));
